import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Ship, Upload, Loader2, Trash2 } from "lucide-react";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";
import { GSTR2BB2BMatch } from "@/components/gst/GSTR2BB2BMatch";
import { GSTR2BBoeMatch } from "@/components/gst/GSTR2BBoeMatch";

const BoeTable = ({ boes }) => (
  <div className="max-h-72 overflow-y-auto">
    <table className="w-full text-xs" data-testid="gstr2b-boe-table">
      <thead><tr className="text-slate-500 uppercase text-[10px] border-b">{["Source", "BoE no.", "BoE date", "Port", "Supplier (SEZ)", "Taxable", "IGST", "Cess"].map((h, i) => <th key={h} className={`${i > 4 ? "text-right" : "text-left"} py-1`}>{h}</th>)}</tr></thead>
      <tbody>
        {boes.map((b, i) => (
          <tr key={`${b.boe_no}-${i}`} className="border-b border-slate-100" data-testid={`gstr2b-boe-${b.boe_no}`}>
            <td className="py-1">{b.source}{b.amended ? " · amended" : ""}</td><td className="font-mono">{b.boe_no}</td><td>{b.boe_date}</td><td>{b.port_code}</td>
            <td className="max-w-[180px] truncate" title={b.supplier_name}>{b.supplier_name || "—"}</td>
            <td className="text-right tabular-nums">{inr(b.taxable)}</td><td className="text-right tabular-nums">{inr(b.igst)}</td><td className="text-right tabular-nums">{inr(b.cess)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const GSTR2BImportCard = ({ extract, onChanged }) => {
  const period = extract?.from?.slice(0, 7);
  const single = period && extract?.to?.slice(0, 7) === period;
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const load = useCallback(() => {
    if (!single) return;
    axios.get(`${API}/gst-returns/gstr2b/${period}`).then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [period, single]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("period", period);
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/gst-returns/gstr2b/upload`, fd)).data;
      toast.success(`GSTR-2B ${r.period}: ${r.count} Bills of Entry · IGST ${inr(r.igst)} — click Regenerate GSTR-3B to apply`);
      load(); setVersion((v) => v + 1); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/gst-returns/gstr2b/${period}`); toast.success("GSTR-2B import data removed — regenerate GSTR-3B"); setDoc(null); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };

  if (!extract) return null;
  return (
    <div className="space-y-4">
    <Card className="border-slate-200 shadow-sm" data-testid="gstr2b-import-card">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Ship className="h-4 w-4" /> 4(A)(1) Import of goods — GSTR-2B (ICEGATE)</CardTitle>
          <p className="text-[11px] text-slate-500 mt-1 max-w-3xl">Customs IGST is booked to G/L 65009030 Custom Duty Charges in SAP, so it never reaches Input IGST. Upload the month's GSTR-2B (Excel or JSON from the GST portal); its IMPG and IMPG-SEZ Bills of Entry fill 4(A)(1).</p>
        </div>
        {single ? (
          <div className="flex items-center gap-2">
            <label className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs cursor-pointer ${busy ? "opacity-60" : "hover:border-sky-400"}`} data-testid="gstr2b-upload-label">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {doc ? "Replace" : "Upload"} GSTR-2B for {period}
              <input type="file" accept=".xlsx,.json" className="hidden" disabled={busy} data-testid="gstr2b-file-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {doc && <Button size="sm" variant="ghost" onClick={remove} data-testid="gstr2b-delete-btn"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
          </div>
        ) : <p className="text-[11px] text-amber-700">GSTR-2B works on a single month — use a one-month extraction.</p>}
      </CardHeader>
      <CardContent>
        {doc ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-700" data-testid="gstr2b-summary">{doc.count} Bills of Entry · taxable {inr(doc.taxable)} · <b>IGST {inr(doc.igst)}</b> · cess {inr(doc.cess)} · {doc.b2b_count ?? 0} B2B/CDNR invoices (tax {inr(doc.b2b_tax || 0)}) · {doc.filename} · uploaded {new Date(doc.uploaded_at).toLocaleString("en-IN")} by {doc.uploaded_by}</p>
            {doc.boes.length > 0 ? <BoeTable boes={doc.boes} /> : <p className="text-xs text-amber-700">The file had no IMPG / IMPG-SEZ entries for this month.</p>}
          </div>
        ) : <p className="text-xs text-slate-500" data-testid="gstr2b-empty">No GSTR-2B uploaded for this month — 4(A)(1) uses tax codes tagged "Import of goods" (none today).</p>}
      </CardContent>
    </Card>
    {doc && <GSTR2BBoeMatch period={period} version={version} />}
    {doc && doc.b2b_count > 0 && <GSTR2BB2BMatch period={period} version={version} />}
    {doc && !doc.b2b_count && <p className="text-[11px] text-amber-700" data-testid="gstr2b-no-b2b">This upload has no B2B / B2B-CDNR invoices — upload the full GSTR-2B to run the supplier-invoice match.</p>}
    </div>
  );
};
